import { Input, Textarea, Label, Button } from '@creanizer/ui'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import type { CharacterBehavior } from '@creanizer/types'

interface BehaviorEditorProps {
  behavior: CharacterBehavior
  onChange: (behavior: CharacterBehavior) => void
}

export function BehaviorEditor({
  behavior,
  onChange
}: BehaviorEditorProps) {
  const [newMannerism, setNewMannerism] = useState('')
  const [newHabit, setNewHabit] = useState('')
  const [newCombatStyle, setNewCombatStyle] = useState('')
  const [newCatchphrase, setNewCatchphrase] = useState('')

  const handleArrayAdd = (field: 'mannerisms' | 'habits' | 'combatStyle' | 'catchphrases', value: string) => {
    if (value) {
      onChange({
        ...behavior,
        [field]: [...(behavior[field] || []), value]
      })
      if (field === 'mannerisms') setNewMannerism('')
      if (field === 'habits') setNewHabit('')
      if (field === 'combatStyle') setNewCombatStyle('')
      if (field === 'catchphrases') setNewCatchphrase('')
    }
  }

  const handleArrayRemove = (field: 'mannerisms' | 'habits' | 'combatStyle' | 'catchphrases', index: number) => {
    onChange({
      ...behavior,
      [field]: (behavior[field] || []).filter((_, i) => i !== index)
    })
  }

  const handleArrayUpdate = (field: 'mannerisms' | 'habits' | 'combatStyle' | 'catchphrases', index: number, value: string) => {
    const updated = [...(behavior[field] || [])]
    updated[index] = value
    onChange({
      ...behavior,
      [field]: updated
    })
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="firstPerson">一人称</Label>
          <Input
            id="firstPerson"
            value={behavior.firstPerson}
            onChange={(e) => onChange({ ...behavior, firstPerson: e.target.value })}
            placeholder="例: 私、俺、僕、わたし"
            className="mt-1"
          />
        </div>
        
        <div>
          <Label htmlFor="socialStyle">対人スタイル</Label>
          <Input
            id="socialStyle"
            value={behavior.socialStyle}
            onChange={(e) => onChange({ ...behavior, socialStyle: e.target.value })}
            placeholder="例: 社交的、内向的"
            className="mt-1"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="speechPattern">話し方の特徴</Label>
        <Textarea
          id="speechPattern"
          value={behavior.speechPattern}
          onChange={(e) => onChange({ ...behavior, speechPattern: e.target.value })}
          placeholder="例: 敬語を使い、丁寧な口調"
          rows={2}
          className="mt-1"
        />
      </div>

      <div>
        <Label className="font-medium">癖・仕草</Label>
        <div className="space-y-2 mt-2">
          {behavior.mannerisms.map((item, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={item}
                onChange={(e) => handleArrayUpdate('mannerisms', index, e.target.value)}
                className="flex-1"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleArrayRemove('mannerisms', index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              value={newMannerism}
              onChange={(e) => setNewMannerism(e.target.value)}
              placeholder="例: 考える時に顎に手を当てる"
              onKeyPress={(e) => e.key === 'Enter' && handleArrayAdd('mannerisms', newMannerism)}
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={() => handleArrayAdd('mannerisms', newMannerism)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label className="font-medium">習慣</Label>
        <div className="space-y-2 mt-2">
          {behavior.habits.map((item, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={item}
                onChange={(e) => handleArrayUpdate('habits', index, e.target.value)}
                className="flex-1"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleArrayRemove('habits', index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              value={newHabit}
              onChange={(e) => setNewHabit(e.target.value)}
              placeholder="例: 毎朝剣の手入れをする"
              onKeyPress={(e) => e.key === 'Enter' && handleArrayAdd('habits', newHabit)}
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={() => handleArrayAdd('habits', newHabit)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="decisionMaking">意思決定の傾向</Label>
        <Textarea
          id="decisionMaking"
          value={behavior.decisionMaking}
          onChange={(e) => onChange({ ...behavior, decisionMaking: e.target.value })}
          placeholder="例: 慎重に情報を集めてから決断する"
          rows={2}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="stressResponse">ストレス時の反応</Label>
        <Textarea
          id="stressResponse"
          value={behavior.stressResponse}
          onChange={(e) => onChange({ ...behavior, stressResponse: e.target.value })}
          placeholder="例: 一人になって考え込む"
          rows={2}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="conflictResolution">対立への対処法</Label>
        <Textarea
          id="conflictResolution"
          value={behavior.conflictResolution}
          onChange={(e) => onChange({ ...behavior, conflictResolution: e.target.value })}
          placeholder="例: 話し合いで解決を図る"
          rows={2}
          className="mt-1"
        />
      </div>

      <div>
        <Label className="font-medium">武器・戦闘スタイル</Label>
        <p className="text-xs text-muted-foreground mb-2">
          戦わないキャラクターの場合は危機時の対応スタンスを記載
        </p>
        <div className="space-y-2">
          {(behavior.combatStyle || []).map((item, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={item}
                onChange={(e) => handleArrayUpdate('combatStyle', index, e.target.value)}
                className="flex-1"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleArrayRemove('combatStyle', index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              value={newCombatStyle}
              onChange={(e) => setNewCombatStyle(e.target.value)}
              placeholder="例: 片手剣と盾、逃げに徹する、交渉で切り抜ける"
              onKeyPress={(e) => e.key === 'Enter' && handleArrayAdd('combatStyle', newCombatStyle)}
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={() => handleArrayAdd('combatStyle', newCombatStyle)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div>
        <Label className="font-medium">口癖・頻出表現</Label>
        <div className="space-y-2 mt-2">
          {(behavior.catchphrases || []).map((item, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={item}
                onChange={(e) => handleArrayUpdate('catchphrases', index, e.target.value)}
                className="flex-1"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleArrayRemove('catchphrases', index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              value={newCatchphrase}
              onChange={(e) => setNewCatchphrase(e.target.value)}
              placeholder="例: なるほどね、ふーん、まあいいか"
              onKeyPress={(e) => e.key === 'Enter' && handleArrayAdd('catchphrases', newCatchphrase)}
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={() => handleArrayAdd('catchphrases', newCatchphrase)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}